import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Target, TrendUp, Brain, Warning, Scales, ArrowRight, CheckCircle, XCircle, BookOpen, Crosshair, Lightning, Gear, Database } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const ComoEncontrarSetup = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Como Encontrar um Setup no Trading | Guia Completo',
      description: 'Aprenda como encontrar e melhorar setups no trading usando backtest, dados e análise consistente.',
      path: '/pt/como-encontrar-um-setup',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/como-encontrar-um-setup'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Setups de Trading</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-[#00FF85] text-sm font-medium mb-4">
          <Crosshair size={16} weight="bold" />
          <span>Setups de Trading</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          Como Encontrar um Setup no Trading
        </h1>

        <p className="text-xl text-gray-400 mb-12">
          (O Que Realmente Funciona)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Todo trader em algum momento busca o <strong className="text-white">"setup perfeito"</strong> — aquele padrão mágico que funciona sempre, em qualquer mercado, com taxa de acerto altíssima. Spoiler: <em className="text-amber-400">ele não existe.</em>
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            O que existe são setups que funcionam <strong className="text-white">em contextos específicos</strong>, com <strong className="text-white">execução consistente</strong>, validados com <strong className="text-white">dados suficientes</strong>. A busca pelo setup perfeito é uma armadilha. A busca por um setup <em>bom o suficiente</em> e <em>bem executado</em> é o caminho real.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Crosshair size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que é um Setup no Trading</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um setup é um padrão repetível que você identifica no mercado e usa como base para tomar decisões de entrada e saída.
            </p>

            <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-[#00FF85] mb-4">Componentes de um Setup:</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Padrão Repetível</h4>
                  <p className="text-gray-400 text-sm">Uma formação ou condição que você consegue identificar consistentemente no gráfico.</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Regras de Entrada e Saída</h4>
                  <p className="text-gray-400 text-sm">Critérios claros: quando entrar, onde colocar stop, onde está o alvo.</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Gestão de Risco</h4>
                  <p className="text-gray-400 text-sm">Quanto arriscar em cada trade, como dimensionar posição.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Sem esses três elementos, você não tem um setup — tem uma ideia vaga que muda a cada trade.
            </p>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que Não Existe Setup Perfeito</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              A busca pelo setup perfeito é uma das maiores armadilhas do trading. E é uma busca sem fim.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">O Mercado Muda</h3>
                </div>
                <p className="text-gray-400 text-sm">O que funciona em bull market pode não funcionar em bear market. O que funciona em alta volatilidade pode falhar em lateralidade. Nenhum setup é imune a mudanças de regime.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Contexto Importa</h3>
                </div>
                <p className="text-gray-400 text-sm">O mesmo padrão gráfico pode ter resultados completamente diferentes dependendo do contexto macro, do ativo, do horário, da liquidez.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Execução Influencia</h3>
                </div>
                <p className="text-gray-400 text-sm">Dois traders usando o mesmo setup terão resultados diferentes baseados na execução. Slippage, timing, disciplina — tudo afeta.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Aceitar que não existe setup perfeito é liberador. Você para de buscar o Santo Graal e começa a focar no que realmente importa: <strong className="text-white">encontrar algo que funciona e executar bem.</strong>
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância do Backtest</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Antes de arriscar dinheiro real, você precisa testar a ideia no histórico. Backtest é o primeiro filtro.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Como fazer backtest corretamente:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Use o TradingView ou plataforma similar:</strong> Volte no tempo, identifique os setups, marque entradas e saídas como se estivesse operando ao vivo.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Teste histórico significativo:</strong> Não olhe só os últimos 30 dias. Teste em diferentes condições de mercado.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Evite viés de confirmação:</strong> Não procure só exemplos que funcionam. Procure TODOS os exemplos do setup.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Valide ideias antes de operar:</strong> Um setup que não funciona no backtest provavelmente não vai funcionar ao vivo.</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Backtest não é garantia de sucesso, mas é um filtro eficiente para eliminar ideias ruins antes que custem dinheiro.
            </p>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Database size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância da Amostra</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um dos erros mais comuns é tirar conclusões com poucos trades. A estatística não perdoa.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-amber-400 mb-4">A realidade estatística:</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-red-400 mb-1">10 trades</p>
                  <p className="text-xs text-gray-500">Ruído puro. Não significa nada.</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-amber-400 mb-1">30-50 trades</p>
                  <p className="text-xs text-gray-500">Tendências começam a aparecer.</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-[#00FF85] mb-1">100+ trades</p>
                  <p className="text-xs text-gray-500">Dados confiáveis para decisões.</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Poucos trades não significam nada. Consistência vem de volume. Você precisa de dados suficientes para separar sinal de ruído.
            </p>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que o Mesmo Setup Funciona Diferente em Cada Ativo</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um setup que funciona em um ativo pode falhar completamente em outro. Isso não é bug — é característica do mercado.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Liquidez</h3>
                <p className="text-gray-400 text-sm">Ativos mais líquidos respeitam melhor níveis técnicos. Ativos ilíquidos têm mais ruído e slippage.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Volatilidade</h3>
                <p className="text-gray-400 text-sm">Setup de rompimento funciona diferente em ativo calmo vs ativo explosivo. Os parâmetros precisam se adaptar.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Comportamento</h3>
                <p className="text-gray-400 text-sm">Cada ativo tem "personalidade". Forex se move diferente de cripto, que se move diferente de ações.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Testar e validar separadamente para cada ativo (ou categoria de ativos) é fundamental para ter expectativas realistas.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">O Problema de Misturar Moedas</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              No mercado cripto, um erro muito comum é agrupar todos os ativos juntos. Isso distorce completamente os resultados.
            </p>

            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-red-400 mb-4">BTC ≠ Altcoins</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Bitcoin</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Alta liquidez</li>
                    <li>• Movimentos mais "limpos"</li>
                    <li>• Referência para o mercado</li>
                  </ul>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Altcoins</h4>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Liquidez variável (algumas muito baixa)</li>
                    <li>• Movimentos mais erráticos</li>
                    <li>• Correlação com BTC, mas com lag</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Agrupar BTC com altcoins de baixo cap e achar que o resultado vale para todos é um erro grave. Separe por categoria, separe por liquidez.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Gear size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Um Mesmo Setup Pode Ter Variações</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um setup base pode ser refinado com diferentes filtros e combinações. Isso cria variações que podem ter performances diferentes.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Exemplo: Setup de Elliott Wave</h3>
              <div className="space-y-3">
                <div className="bg-gray-900/50 rounded-lg p-4 flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                  <div>
                    <p className="font-semibold text-white">Elliott + RSI</p>
                    <p className="text-gray-400 text-sm">Usa divergência de RSI para confirmar fim de onda corretiva.</p>
                  </div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                  <div>
                    <p className="font-semibold text-white">Elliott + CVD</p>
                    <p className="text-gray-400 text-sm">Usa Cumulative Volume Delta para confirmar força da onda impulsiva.</p>
                  </div>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#00FF85]"></div>
                  <div>
                    <p className="font-semibold text-white">Elliott + Fibonacci + Volume</p>
                    <p className="text-gray-400 text-sm">Combina níveis de Fib com análise de volume em zonas específicas.</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Cada variação pode ter win rate e RR diferentes. Testar separadamente permite entender qual combinação funciona melhor para você.
            </p>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Scales size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Mais Indicadores ≠ Melhor Setup</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um erro comum de traders iniciantes é empilhar indicadores achando que isso melhora o setup. Raramente funciona assim.
            </p>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-amber-400 mb-4">O problema dos filtros excessivos:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Mais filtros = menos trades:</strong> Cada filtro adicional elimina oportunidades. Você pode acabar com setup que dá 2 sinais por mês.</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Menos trades não garante qualidade:</strong> Ter poucos trades significa amostra pequena. Amostra pequena significa ruído estatístico.</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Overfitting:</strong> Muitos filtros podem fazer o setup parecer perfeito no histórico, mas falhar no futuro.</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O equilíbrio é essencial. Um setup simples com regras claras geralmente supera um setup complexo cheio de condições.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">O Que Realmente Faz um Setup Funcionar</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Depois de tudo isso, o que realmente importa para um setup funcionar?
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Dados</h3>
                <p className="text-gray-400 text-sm">Validação com volume suficiente de trades. Sem dados, você está operando no achismo.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Consistência</h3>
                <p className="text-gray-400 text-sm">Execução igual, trade após trade. Sem consistência, não há como avaliar se o setup funciona.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Execução</h3>
                <p className="text-gray-400 text-sm">Seguir as regras. Entrada, stop, alvo — tudo conforme definido. Execução ruim mata qualquer setup.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Contexto</h3>
                <p className="text-gray-400 text-sm">Saber onde o setup funciona melhor. Qual ativo, qual condição de mercado, qual timeframe.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Um setup "médio" bem executado com dados consistentes supera um setup "perfeito" mal executado sem validação.
            </p>
          </section>

          {/* Seção 10 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">O Objetivo Não é Perfeição, é Evolução</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O melhor setup que você vai ter daqui a um ano não é o que você tem hoje. A evolução é contínua.
            </p>

            <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-purple-400 mb-4">O ciclo de evolução de setups:</h3>
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
                <span className="px-4 py-2 bg-gray-900/50 rounded-lg">Identifica padrão</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-gray-900/50 rounded-lg">Testa no histórico</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-gray-900/50 rounded-lg">Opera ao vivo</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-gray-900/50 rounded-lg">Coleta dados</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-gray-900/50 rounded-lg">Analisa resultados</span>
                <ArrowRight size={16} className="text-gray-600" />
                <span className="px-4 py-2 bg-[#00FF85]/20 text-[#00FF85] rounded-lg">Ajusta e melhora</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                <span className="text-gray-300"><strong className="text-white">Melhore setups existentes:</strong> Em vez de buscar novos setups, refine os que você já tem.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                <span className="text-gray-300"><strong className="text-white">Entenda onde funciona melhor:</strong> Qual ativo? Qual timeframe? Qual condição de mercado?</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle size={20} className="text-purple-400 mt-1 flex-shrink-0" />
                <span className="text-gray-300"><strong className="text-white">Ajuste continuamente:</strong> O mercado muda, seus setups precisam se adaptar.</span>
              </li>
            </ul>
          </section>

          {/* GENI Mention - Importante */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Compare Setups com Dados Reais</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Com o <strong className="text-[#00FF85]">GENI</strong>, traders conseguem comparar diferentes setups entre ativos e grupos de moedas de forma automática e visual, facilitando a identificação do que realmente funciona.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Inclusive, fica muito mais fácil <strong className="text-white">melhorar setups já bons</strong> e entender <strong className="text-white">qual funciona melhor em cada tipo de mercado</strong>. Sem planilhas, sem achismo — apenas dados claros.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Encontre Seu Setup com Dados, Não com Sorte</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Encontrar um bom setup não é sobre sorte, mas sobre análise, dados e melhoria contínua. Pare de buscar perfeição e comece a construir consistência.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Existe setup perfeito?</h3>
                <p className="text-gray-400 text-sm">Não. O mercado muda, contextos mudam, e nenhum setup funciona 100% do tempo. O objetivo é encontrar um setup que funciona bem o suficiente em condições específicas e executá-lo consistentemente.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Quantos trades para validar um setup?</h3>
                <p className="text-gray-400 text-sm">Como referência: 20-30 para sinais iniciais, 50-100 para conclusões mais sólidas, 100+ para alta confiança. Quanto mais dados, mais confiável a validação.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Posso usar o mesmo setup em todas as moedas?</h3>
                <p className="text-gray-400 text-sm">Não necessariamente. Cada ativo tem liquidez, volatilidade e comportamento diferentes. Teste e valide separadamente para cada categoria de ativos.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Mais indicadores melhoram o setup?</h3>
                <p className="text-gray-400 text-sm">Não automaticamente. Mais indicadores significam menos sinais e maior risco de overfitting. Um setup simples e bem executado geralmente supera um setup complexo cheio de filtros.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/quantos-trades-para-validar-estrategia"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Quantos Trades para Validar</h3>
                <p className="text-gray-500 text-sm">Entenda a importância da amostra estatística.</p>
              </Link>

              <Link
                to="/pt/como-melhorar-risk-reward"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Melhorar Risk Reward</h3>
                <p className="text-gray-500 text-sm">Técnicas para melhorar sua relação risco/retorno.</p>
              </Link>

              <Link
                to="/pt/profit-factor-trading"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Profit Factor no Trading</h3>
                <p className="text-gray-500 text-sm">Entenda a métrica que mostra a qualidade real do setup.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComoEncontrarSetup;
